'use client';

import React from 'react';
import {
  Button,
  GoogleLogoIcon,
  LockIcon,
} from '@outmarket/ui';
import { Mail } from 'lucide-react';
import { PiMicrosoftOutlookLogoFill } from 'react-icons/pi';
import { useEmailConnectionStatus } from '@/hooks/api/integrations/email/useEmailConnection';
import { useEmailConnect } from '@/hooks/api/integrations/email/useEmailConnect';
import { useEmailStore } from '@/stores/emailStore';
import { useFeatureUsageCheck } from '@/hooks';
import { useAuth } from '@/context/AuthContext';
import { EMAIL_PROVIDERS } from '@/utils/constants';
import { isInternalDomain } from '@/utils/isInternalDomain';
import { IntegrationCard } from '../IntegrationCard';
import dynamic from 'next/dynamic';
import type { EmailProvider } from '@/types/email';

const SendTestEmailModal = dynamic(() => import('./SendTestEmailModal'), { ssr: false });

function ProviderLogo({ provider, size = 40 }: { provider?: EmailProvider; size?: number }): React.ReactElement | null {
  if (provider === EMAIL_PROVIDERS.GOOGLE) return <GoogleLogoIcon size={size} />;
  if (provider === EMAIL_PROVIDERS.MICROSOFT) return <PiMicrosoftOutlookLogoFill size={size} color="#0078D4" />;
  return null;
}

interface EmailIntegrationCardWrapperProps {
  description: string;
}

export function EmailIntegrationCardWrapper({
  description,
}: EmailIntegrationCardWrapperProps): React.ReactElement {
  const { data: connectionStatus } = useEmailConnectionStatus();
  // Shared OAuth flow (popup + BroadcastChannel + code exchange + toasts +
  // status invalidation) — the same hook the Agent Platform inline connect card
  // uses, so both surfaces stay byte-identical.
  const { connect: handleConnect, isConnecting } = useEmailConnect();
  const openTestEmailModal = useEmailStore((state) => state.openTestEmailModal);
  const { isModuleAvailable } = useFeatureUsageCheck('email_integration');
  const isPlanLocked = !isModuleAvailable;
  const { user } = useAuth();
  const isInternalUser = isInternalDomain(user?.email);

  const isConnected = !!(connectionStatus?.connected && !connectionStatus?.expired);
  const isExpired = !!(connectionStatus?.connected && connectionStatus?.expired);

  const hasProvider = (isConnected || isExpired) && !!connectionStatus?.provider;
  const title = (isConnected || isExpired) && connectionStatus?.email
    ? connectionStatus.email
    : 'Email';

  const logo = hasProvider
    ? <ProviderLogo provider={connectionStatus!.provider} />
    : <Mail className="w-10 h-10 text-neutral03" />;

  function renderActions(): React.ReactNode {
    if (isPlanLocked) {
      return (
        <span className="flex items-center gap-1.5 text-xs text-neutral04">
          <LockIcon className="w-3.5 h-3.5" />
          Not included
        </span>
      );
    }
    if (isConnected) {
      return (
        <Button variant="outline" size="sm" className="text-primary" onClick={openTestEmailModal}>
          Manage
        </Button>
      );
    }

    const buttonLabel = isExpired
      ? (isConnecting ? 'Reconnecting...' : 'Reconnect')
      : (isConnecting ? 'Connecting...' : 'Connect');

    return (
      <Button
        variant="outline"
        size="sm"
        className="text-primary"
        onClick={handleConnect}
        isLoading={isConnecting}
        disabled={isConnecting}
      >
        {buttonLabel}
      </Button>
    );
  }

  const supportedHint = 'Currently supports Microsoft Outlook. Google & IMAP support coming soon.';
  const showSupportedHint = !isConnected && !isExpired && !isPlanLocked && !isInternalUser;
  const cardDescription = showSupportedHint
    ? `${description} ${supportedHint}`
    : description;

  return (
    <>
      <IntegrationCard
        title={title}
        description={cardDescription}
        logo={logo}
        scope="personal"
        isConnected={isConnected || isExpired}
        statusLabel={isExpired ? 'Connection Failed' : 'Active'}
        statusVariant={isExpired ? 'error' : 'success'}
        isPlanLocked={isPlanLocked}
        actions={renderActions()}
      />
      <SendTestEmailModal />
    </>
  );
}
